import { createFileRoute } from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { safeJsonLd } from "@/lib/structured-data";
import { getNationalStructuredData } from "@/lib/structured-data/national";
import { fullResults } from "@/lib/site-data";
import { Header } from "@/components/site/Header";
import { HeroResults } from "@/components/site/HeroResults";
import { Footer } from "@/components/site/Footer";

const Results = lazy(() => import("@/components/site/Results").then(m => ({ default: m.Results })));
const NorwoodQuiz = lazy(() => import("@/components/site/NorwoodQuiz").then(m => ({ default: m.NorwoodQuiz })));
const Guarantee = lazy(() => import("@/components/site/Guarantee").then(m => ({ default: m.Guarantee })));
const Faq = lazy(() => import("@/components/site/Faq").then(m => ({ default: m.Faq })));
const QuoteForm = lazy(() => import("@/components/site/QuoteForm").then(m => ({ default: m.QuoteForm })));

export const Route = createFileRoute("/results/")({
  head: () => ({
    meta: [
      { title: "Hair Transplant Results — Before & After | RESTORE® Hair" },
      {
        name: "description",
        content:
          "See real RESTORE® hair transplant before & after results from NFL legends, pro athletes, celebrities and everyday clients. 100% authentic. No photoshopping.",
      },
      { property: "og:title", content: "Hair Transplant Results — Before & After | RESTORE® Hair" },
      {
        property: "og:description",
        content:
          "Real RESTORE® hair transplant before & after results from athletes, celebrities and everyday clients. Over 20,000 procedures.",
      },
      { property: "og:url", content: "https://restorehair.com/results/" },
    ],
    links: [{ rel: "canonical", href: "https://restorehair.com/results/" }],
    scripts: [
      {
        type: "application/ld+json",
        children: safeJsonLd({ "@graph": getNationalStructuredData() }),
      },
    ],
  }),
  component: ResultsPage,
});

function ResultsPage() {
  return (
    <div className="min-h-screen bg-background font-sans">
      <Header />
      <main>
        <HeroResults />
        <Suspense fallback={null}>
          <Results
            items={fullResults}
            initialCount={24}
            showSeeMore
            batchSizes={[24, 24, 24, 9]}
            columns={3}
            seeMoreLabel="See More Hair Transplant Results"
            eyebrow="Hair Transplant Results: Before and After"
            heading={
              <span className="mb-[10px] block text-[30px] leading-none font-normal">
                100% authentic RESTORE hair transplant clients.
              </span>
            }
            subheading={
              <span className="block text-[30px] leading-none font-bold">
                No photoshopping. No trick photography.
              </span>
            }
            description={null}
            headlineAfterButton={
              <>
                <span className="block text-[30px] leading-none font-normal text-foreground">
                  100% authentic RESTORE® hair transplant surgery clients.
                </span>
                <span className="block text-[30px] leading-none font-bold text-foreground">
                  No photoshopping. No trick photography.
                </span>
              </>
            }
          />
          <ClientStories />
          <NorwoodQuiz />
          <Guarantee variant="results" />
          <Faq />
          <QuoteForm />
        </Suspense>
      </main>
      <Footer />
    </div>
  );
}
// Links to every results story page. The gallery above renders 24 cards on
// the server, so stories further down had no crawlable link.
const STORIES = [
  { name: "Brian Urlacher", href: "/results/brian-urlacher/" },
  { name: "Deion Sanders", href: "/results/deion-sanders/" },
  { name: "Zach Johnson", href: "/results/zach-johnson/" },
  { name: "Mike Bibby", href: "/results/mike-bibby/" },
  { name: "Pat Hoberg", href: "/results/pat-hoberg/" },
  { name: "Ian Happ", href: "/results/ian-happ/" },
  { name: "Jim Cornelison", href: "/results/jim-cornelison/" },
  { name: "Matt Forte", href: "/results/matt-forte/" },
  { name: "Bryan Bickell", href: "/results/bryan-bickell/" },
  { name: "Danny Kanell", href: "/results/danny-kanell/" },
  { name: "Heath Evans", href: "/results/heath-evans/" },
  { name: "Kevin Streelman", href: "/results/kevin-streelman/" },
  { name: "Kevin VanDam", href: "/results/kevin-vandam/" },
  { name: "Ryne Sandberg", href: "/results/ryne-sandberg/" },
  { name: "Robert L.", href: "/results/robert-l/" },
];

function ClientStories() {
  return (
    <section className="bg-background py-10">
      <div className="mx-auto max-w-5xl px-4 text-center lg:px-8">
        <h2 className="text-2xl font-bold text-foreground">Client Stories</h2>
        <nav aria-label="Client stories" className="mt-5 flex flex-wrap justify-center gap-x-5 gap-y-2">
          {STORIES.map((s) => (
            <a key={s.href} href={s.href} className="text-primary underline underline-offset-4 hover:no-underline">
              {s.name}
            </a>
          ))}
        </nav>
      </div>
    </section>
  );
}
